/**
 * Download-on-demand chart PACKS — orchestration.
 *
 * Loads the remote packs manifest, then per pack lets the user:
 *   - View online: stream the pack's PMTiles from the host (no download).
 *   - Download: save the pack to the device for offline use.
 *   - Delete: remove a downloaded pack.
 * The charts of every pack that's currently on (online or downloaded) are merged
 * and published to the chart-sources store, where resolveProvider folds them into
 * the Antares provider. Downloaded pack ids are persisted so offline packs come
 * back on next launch.
 */
import { create } from 'zustand';
import type { ChartPack, RasterChart } from '@/chart-sources';
import { isVectorPack } from '@/chart-sources/providers';
import { TrainingChartSource } from '@/chart-sources/training';
import type { CellMeta, ChartSource } from '@/types';
import {
  chartFileName,
  fetchPacksManifest,
  loadPackCellManifest,
  loadPackChartManifest,
  remotePackCharts,
} from '@/chart-sources/packManifest';
import {
  DownloadedCellSource,
  downloadCellPack,
  downloadPack,
  localPackCharts,
  deletePack,
  isCellPackDownloaded,
  isPackDownloaded,
  cellIdentity,
  cellFileName,
  chartIdentity,
  correctionsBehind,
  readInstalled,
  writePackRecord,
  readPackRecord,
  type DownloadProgress,
} from '@/chart-sources/packDownload';
import { PACKS_MANIFEST_URL, PACKS_MANIFEST_URLS } from '@/chart-sources/packsConfig';
import {
  packsIntersecting,
  packsAlongRoute,
  type LonLatBox,
} from '@/chart-sources/packSelection';
import { useChartSourcesStore } from './chartSources';

export type PackStatus = 'idle' | 'online' | 'downloading' | 'downloaded';

/** State of loading the top-level packs manifest (so the UI can tell a failed
 *  fetch apart from a genuinely empty list). */
export type ManifestStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PackRuntime {
  status: PackStatus;
  /** Download progress 0..1 while status is 'downloading'. */
  progress: number;
  /** Bytes saved so far across the pack (from the native download event). */
  bytesDownloaded: number;
  /** Total bytes for the pack (from the manifest's sizeBytes). */
  bytesTotal: number;
  /**
   * How many of a downloaded pack's charts or cells have been corrected since it
   * was saved. Zero when up to date, and only meaningful once downloaded.
   */
  behind: number;
  error: string | null;
}

const LS_DOWNLOADED = 'acp.chartpacks.downloaded.v1';
const LS_MODE = 'acp.chartpacks.mode.v1';

/**
 * How the pack-backed providers behave (fb-msn0j0jv-3):
 *   'online'      stream whatever the view needs, when there is signal;
 *   'downloaded'  the downloads are the chart, and online only fills in
 *                 beyond their edge, with a notice each time we cross it.
 */
export type PackProviderMode = 'online' | 'downloaded';

function loadMode(): PackProviderMode {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_MODE) : null;
    return raw === 'downloaded' ? 'downloaded' : 'online';
  } catch {
    return 'online';
  }
}

function loadDownloadedIds(): string[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_DOWNLOADED) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
function saveDownloadedIds(ids: string[]): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_DOWNLOADED, JSON.stringify(ids));
  } catch {
    /* ignore quota / private mode */
  }
}

interface ChartPacksState {
  /** One mode for all pack-backed providers; per-provider splits can wait for a
   *  second pack family to actually need one. */
  mode: PackProviderMode;
  setMode: (mode: PackProviderMode) => void;
  /**
   * The drag-a-box flow. `areaSelectArmed` is set by the panel and watched by
   * MapView, the same arm-and-watch pattern as placing the anchor on the
   * chart; the finished box comes back through stageBox.
   */
  areaSelectArmed: boolean;
  armAreaSelect: () => void;
  cancelAreaSelect: () => void;
  /**
   * A staged selection: the packs an area resolved to, waiting for one
   * deliberate "Download" on the confirm card. Staging is free; bytes are not.
   */
  stagedPackIds: string[] | null;
  stageBox: (box: LonLatBox) => void;
  stageAlongRoute: (waypoints: [number, number][], widthNm: number) => void;
  clearStaged: () => void;
  downloadStaged: () => Promise<void>;
  manifestUrl: string;
  packs: ChartPack[];
  manifestLoaded: boolean;
  manifestStatus: ManifestStatus;
  runtime: Record<string, PackRuntime>;
  /** Per-pack raw chart manifest cache (bare file names). */
  raw: Record<string, RasterChart[]>;
  /** Per-pack resolved charts currently contributing to the map. */
  active: Record<string, RasterChart[]>;
  /** Per-pack cell source for the vector packs that are on. */
  activeCells: Record<string, ChartSource>;
  /** Per-pack cell list cache for the vector packs. */
  cells: Record<string, CellMeta[]>;

  /**
   * Bring back every pack saved on this device, reading each pack's OWN manifest
   * off the disk. No network, no remote list. See restoreDownloaded below.
   */
  restoreDownloaded: () => Promise<void>;
  loadManifest: (url?: string) => Promise<void>;
  viewOnline: (pack: ChartPack) => Promise<void>;
  turnOff: (pack: ChartPack) => void;
  download: (pack: ChartPack) => Promise<void>;
  remove: (pack: ChartPack) => Promise<void>;
}

function defaultRuntime(): PackRuntime {
  return { status: 'idle', progress: 0, bytesDownloaded: 0, bytesTotal: 0, behind: 0, error: null };
}

export const useChartPacksStore = create<ChartPacksState>((set, get) => {
  /** Push the merged active charts to the chart-sources store. */
  const republish = () => {
    const merged = Object.values(get().active).flat();
    useChartSourcesStore.getState().setPackCharts(merged);
    useChartSourcesStore.getState().setPackCellSources(Object.values(get().activeCells));
  };
  /**
   * A cell source for a vector pack, reading its cell list from the pack's own
   * manifest and its cells from the pack's host. It is the same adapter the
   * bundled cells use, so residency, the LRU and the renderer all treat a pack's
   * cells exactly like any other.
   */
  const cellSourceFor = (pack: ChartPack): ChartSource =>
    new TrainingChartSource(pack.baseUrl, {
      id: `pack:${pack.id}`,
      name: pack.region,
      manifestUrl: pack.cellsUrl ?? pack.chartsUrl,
    });
  /** Merge a runtime patch for one pack. */
  const patch = (id: string, p: Partial<PackRuntime>) =>
    set((st) => ({ runtime: { ...st.runtime, [id]: { ...(st.runtime[id] ?? defaultRuntime()), ...p } } }));
  /** Turn a download progress tick into a runtime patch, for either pack kind. */
  const report = (pack: ChartPack, p: DownloadProgress) => {
    // Prefer honest byte progress against the manifest size; fall back to a
    // per-file count if the pack size is missing so the bar still moves.
    const frac =
      pack.sizeBytes > 0 ? Math.min(1, p.bytes / pack.sizeBytes) : p.total ? p.done / p.total : 0;
    patch(pack.id, { progress: frac, bytesDownloaded: p.bytes, bytesTotal: pack.sizeBytes });
  };
  /** Fetch (and cache) a vector pack's cell list. */
  const packCells = async (pack: ChartPack) => {
    const have = get().cells[pack.id];
    if (have) return have;
    const cells = await loadPackCellManifest(pack);
    set((st) => ({ cells: { ...st.cells, [pack.id]: cells } }));
    return cells;
  };
  /** Fetch (and cache) a pack's raw chart manifest. */
  const rawCharts = async (pack: ChartPack): Promise<RasterChart[]> => {
    const have = get().raw[pack.id];
    if (have) return have;
    const charts = await loadPackChartManifest(pack);
    set((st) => ({ raw: { ...st.raw, [pack.id]: charts } }));
    return charts;
  };

  /** The staged set: intersecting packs not already on the device. */
  const stageFrom = (picked: { id: string }[]) => {
    const rt = get().runtime;
    const ids = picked
      .map((p) => p.id)
      .filter((id) => rt[id]?.status !== 'downloaded' && rt[id]?.status !== 'downloading');
    set({ stagedPackIds: ids, areaSelectArmed: false });
  };

  return {
    mode: loadMode(),
    setMode: (mode) => {
      try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(LS_MODE, mode);
      } catch {
        /* ignore */
      }
      set({ mode });
    },

    areaSelectArmed: false,
    armAreaSelect: () => set({ areaSelectArmed: true, stagedPackIds: null }),
    cancelAreaSelect: () => set({ areaSelectArmed: false }),

    stagedPackIds: null,
    stageBox: (box) => stageFrom(packsIntersecting(get().packs, box)),
    stageAlongRoute: (waypoints, widthNm) =>
      stageFrom(packsAlongRoute(get().packs, waypoints, widthNm)),
    clearStaged: () => set({ stagedPackIds: null }),
    /**
     * Download the staged packs one after another rather than all at once:
     * pack downloads are big, and three at a time over a boat connection is
     * how all three end up half-finished.
     */
    downloadStaged: async () => {
      const ids = get().stagedPackIds ?? [];
      set({ stagedPackIds: null });
      for (const id of ids) {
        const pack = get().packs.find((p) => p.id === id);
        if (pack) await get().download(pack);
      }
    },

    manifestUrl: PACKS_MANIFEST_URL,
    packs: [],
    manifestLoaded: false,
    manifestStatus: 'idle',
    runtime: {},
    raw: {},
    active: {},
    activeCells: {},
    cells: {},

    /**
     * Bring back the packs saved on this device, reading each pack's own
     * manifest off the disk (packDownload's PackRecord). No network at all.
     *
     * This used to be folded into loadManifest, which meant a downloaded pack
     * could only come back if the REMOTE list could be fetched first. So with no
     * signal the list failed, the restore never ran, and charts sitting on the
     * device were unreachable: an offline navigation app losing its downloaded
     * charts precisely when offline. Putting the licensed packs behind a token
     * gate made it bite far more often, because now the list can fail while the
     * network is perfectly fine.
     *
     * Restored packs are also merged into `packs`, so the store still lists what
     * we hold (and can delete it) when the remote list is unreachable.
     */
    restoreDownloaded: async () => {
      const ids = loadDownloadedIds();
      if (ids.length === 0) return;
      const restored: ChartPack[] = [];
      for (const id of ids) {
        try {
          const record = await readPackRecord(id);
          if (!record) continue;
          const { pack } = record;
          if (isVectorPack(pack)) {
            const cells = record.cells ?? [];
            if (cells.length === 0 || !(await isCellPackDownloaded(pack, cells))) continue;
            const source = new DownloadedCellSource(pack, cells);
            set((st) => ({
              activeCells: { ...st.activeCells, [pack.id]: source },
              cells: { ...st.cells, [pack.id]: cells },
            }));
          } else {
            const raw = record.charts ?? [];
            if (raw.length === 0 || !(await isPackDownloaded(pack, raw))) continue;
            const charts = await localPackCharts(pack, raw);
            set((st) => ({
              active: { ...st.active, [pack.id]: charts },
              raw: { ...st.raw, [pack.id]: raw },
            }));
          }
          restored.push(pack);
          patch(pack.id, { status: 'downloaded' });
        } catch {
          /* a pack that will not restore is left for the remote list to sort out */
        }
      }
      if (restored.length > 0) {
        // Merge rather than replace: a manifest that has already landed keeps
        // its (fresher) entries, and restored packs fill in what it is missing.
        set((st) => {
          const byId = new Map(restored.map((p) => [p.id, p]));
          for (const p of st.packs) byId.set(p.id, p);
          return { packs: [...byId.values()], manifestLoaded: true };
        });
        republish();
      }
    },

    loadManifest: async (url) => {
      // One explicit URL if given, otherwise every list the app knows about:
      // the raster packs and the vector ones live at separate URLs.
      const urls = url ? [url] : PACKS_MANIFEST_URLS;
      const manifestUrl = url ?? get().manifestUrl;
      set({ manifestUrl, manifestStatus: 'loading' });
      const results = await Promise.all(
        urls.map(async (u) => {
          try {
            const packs = await fetchPacksManifest(u);
            console.info(`[packs] ${u} -> ${packs.length} packs`);
            return packs;
          } catch (err) {
            // Named out loud, because the panel can only say "couldn't load" and
            // the usual cause is the URL being the one you thought you changed.
            console.warn(`[packs] ${u} -> ${String((err as Error)?.message ?? err)}`);
            return null;
          }
        }),
      );
      if (results.every((r) => r === null)) {
        // Nothing answered (offline, or none of the lists are published yet).
        // Keep any packs we already had and let the UI offer a retry.
        set({ manifestStatus: 'error', manifestLoaded: true });
        return;
      }
      // One list being unreachable should not hide the packs from the others, so
      // a null is skipped rather than treated as a failure of the whole load.
      const byId = new Map<string, ChartPack>();
      // Seed with what we already hold (restoreDownloaded may have put saved
      // packs here), so a list that no longer carries a pack we have on disk
      // cannot make that pack vanish from the store.
      for (const p of get().packs) byId.set(p.id, p);
      for (const r of results) for (const p of r ?? []) byId.set(p.id, p);
      const packs: ChartPack[] = [...byId.values()];
      const runtime: Record<string, PackRuntime> = { ...get().runtime };
      for (const p of packs) if (!runtime[p.id]) runtime[p.id] = defaultRuntime();
      set({ manifestUrl, packs, runtime, manifestLoaded: true, manifestStatus: 'ready' });

      // Re-register packs saved offline on a previous run: verify the files are
      // still there, rebuild their local charts, and switch them on.
      const savedIds = new Set(loadDownloadedIds());
      for (const pack of packs) {
        if (!savedIds.has(pack.id)) continue;
        try {
          if (isVectorPack(pack)) {
            const cells = await packCells(pack);
            if (cells.length > 0 && (await isCellPackDownloaded(pack, cells))) {
              const source = new DownloadedCellSource(pack, cells);
              set((st) => ({ activeCells: { ...st.activeCells, [pack.id]: source } }));
              // Compare what is on disk against what the pack now publishes, so a
              // correction issued since the download is visible rather than silent.
              const installed = await readInstalled(pack);
              const behind = correctionsBehind(
                cells.map((c) => ({ file: cellFileName(c), identity: cellIdentity(c) })),
                installed,
              );
              // Backfill the pack's own manifest for anything downloaded before
              // records existed, so its NEXT restore needs no network.
              await writePackRecord({ pack, cells });
              patch(pack.id, { status: 'downloaded', behind });
            }
            continue;
          }
          const raw = await rawCharts(pack);
          if (raw.length > 0 && (await isPackDownloaded(pack, raw))) {
            const charts = await localPackCharts(pack, raw);
            set((st) => ({ active: { ...st.active, [pack.id]: charts } }));
            const installed = await readInstalled(pack);
            const behind = correctionsBehind(
              raw.map((c) => ({ file: chartFileName(c.path), identity: chartIdentity(c) })),
              installed,
            );
            await writePackRecord({ pack, charts: raw });
            patch(pack.id, { status: 'downloaded', behind });
          }
        } catch {
          /* skip a pack that won't restore; user can re-download */
        }
      }
      republish();
    },

    viewOnline: async (pack) => {
      patch(pack.id, { error: null });
      try {
        if (isVectorPack(pack)) {
          // Check the cell list is really there before switching the pack on, so
          // a bad URL shows as an error rather than as a chart that never draws.
          const cells = await packCells(pack);
          if (cells.length === 0) throw new Error('No cells in this pack');
          const source = cellSourceFor(pack);
          set((st) => ({ activeCells: { ...st.activeCells, [pack.id]: source } }));
          patch(pack.id, { status: 'online' });
          republish();
          return;
        }
        const raw = await rawCharts(pack);
        if (raw.length === 0) throw new Error('No charts in this pack');
        const charts = remotePackCharts(pack, raw);
        set((st) => ({ active: { ...st.active, [pack.id]: charts } }));
        patch(pack.id, { status: 'online' });
        republish();
      } catch (e) {
        patch(pack.id, { error: e instanceof Error ? e.message : 'Could not load pack' });
      }
    },

    turnOff: (pack) => {
      // Only turns off an ONLINE pack; a downloaded pack is turned off by Delete.
      if (get().runtime[pack.id]?.status !== 'online') return;
      set((st) => {
        const active = { ...st.active };
        delete active[pack.id];
        const activeCells = { ...st.activeCells };
        delete activeCells[pack.id];
        return { active, activeCells };
      });
      patch(pack.id, { status: 'idle' });
      republish();
    },

    download: async (pack) => {
      patch(pack.id, {
        status: 'downloading',
        progress: 0,
        bytesDownloaded: 0,
        bytesTotal: pack.sizeBytes,
        error: null,
      });
      try {
        if (isVectorPack(pack)) {
          const cells = await packCells(pack);
          if (cells.length === 0) throw new Error('No cells in this pack');
          await downloadCellPack(pack, cells, { onProgress: (p) => report(pack, p) });
          // Read the saved cells back to prove they are really there before
          // calling the pack downloaded. A pack that says "saved offline" and
          // then draws nothing at sea is the failure worth ruling out.
          if (!(await isCellPackDownloaded(pack, cells))) {
            throw new Error('Some cells did not save');
          }
          const source = new DownloadedCellSource(pack, cells);
          set((st) => ({ activeCells: { ...st.activeCells, [pack.id]: source } }));
          // The pack's own manifest, so it can be brought back with no network.
          await writePackRecord({ pack, cells });
          patch(pack.id, {
            status: 'downloaded',
            progress: 1,
            bytesDownloaded: pack.sizeBytes,
            bytesTotal: pack.sizeBytes,
            behind: 0,
          });
          saveDownloadedIds([...new Set([...loadDownloadedIds(), pack.id])]);
          republish();
          return;
        }
        const raw = await rawCharts(pack);
        if (raw.length === 0) throw new Error('No charts in this pack');
        await downloadPack(pack, raw, { onProgress: (p) => report(pack, p) });
        const charts = await localPackCharts(pack, raw);
        set((st) => ({ active: { ...st.active, [pack.id]: charts } }));
        await writePackRecord({ pack, charts: raw });
        patch(pack.id, {
          status: 'downloaded',
          progress: 1,
          bytesDownloaded: pack.sizeBytes,
          bytesTotal: pack.sizeBytes,
          behind: 0,
        });
        saveDownloadedIds([...new Set([...loadDownloadedIds(), pack.id])]);
        republish();
      } catch (e) {
        patch(pack.id, { status: 'idle', error: e instanceof Error ? e.message : 'Download failed' });
      }
    },

    remove: async (pack) => {
      await deletePack(pack);
      saveDownloadedIds(loadDownloadedIds().filter((id) => id !== pack.id));
      set((st) => {
        const active = { ...st.active };
        delete active[pack.id];
        const activeCells = { ...st.activeCells };
        delete activeCells[pack.id];
        return { active, activeCells };
      });
      patch(pack.id, { status: 'idle', progress: 0, bytesDownloaded: 0, bytesTotal: 0, behind: 0 });
      republish();
    },
  };
});
